import _ from "lodash";
import camelize from "camelize";
import queryString from "query-string";
import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { ToastProvider, useToasts } from "react-toast-notifications";
import { useQuery, useMutation, useQueryCache } from "react-query";
import { Message, Icon, Button, Segment, Header, Checkbox, Table, Grid, Loader } from "semantic-ui-react";
import JsonApi from "../../../utils/json_api";
import { setJwt } from "../../../actions/identity";
import identitySelectors from "../../../selectors/identity";
import {
  subscribeAllEmails,
  subscribeEmailGroup,
  unsubscribeEmailGroup,
  deleteSubscription,
  postSubscription,
  getSubscriptions,
  getEmailSubscriptionGroups,
} from "../../../queries/communications";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";

const SubscriptionsList = ({ email }: any) => {
  const { addToast } = useToasts();
  const networkHeaders = useNetworkHeaders();
  const subscriptionsQuery = useQuery("communications.subscriptions", getSubscriptions(networkHeaders));
  const emailSubscriptionsQuery = useQuery(
    ["communications.subscriptions.email", email],
    getEmailSubscriptionGroups(networkHeaders),
    { enabled: !!email },
  );
  const isEmailUnsubscribed = _.get(emailSubscriptionsQuery, "data.response.status") === 404;

  const queryCache = useQueryCache();
  const [subscribe] = useMutation(postSubscription(networkHeaders), {
    onSuccess: () => queryCache.invalidateQueries("communications.subscriptions"),
  });
  const [unsubscribe] = useMutation(deleteSubscription(networkHeaders), {
    onSuccess: () => queryCache.invalidateQueries("communications.subscriptions"),
  });
  const [unsubscribeEmail] = useMutation(unsubscribeEmailGroup(networkHeaders, email as string), {
    onSuccess: () => queryCache.invalidateQueries("communications.subscriptions.email"),
  });
  const [subscribeEmail] = useMutation(subscribeEmailGroup(networkHeaders, email as string), {
    onSuccess: () => queryCache.invalidateQueries("communications.subscriptions.email"),
  });
  const [subscribeAll] = useMutation(subscribeAllEmails(networkHeaders), {
    onSuccess: () => queryCache.invalidateQueries("communications.subscriptions.email"),
  });

  const isLoading = _.some([subscriptionsQuery.isLoading, emailSubscriptionsQuery.isLoading]);

  if (isLoading) {
    return (
      <Grid verticalAlign='middle' centered>
        <div style={{ margin: "auto", height: "100%" }}>
          <Loader active={true}>Loading...</Loader>
        </div>
      </Grid>
    );
  }

  return (
    <>
      {isEmailUnsubscribed && <Button onClick={async () => await subscribeAll(email)}>Sign up for emails</Button>}
      <Table collapsing>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell collapsing>
              <Icon name='mobile' />
              Mobile
            </Table.HeaderCell>
            <Table.HeaderCell collapsing>
              <Icon name='mail' />
              Email
            </Table.HeaderCell>
            <Table.HeaderCell>Subscription Group</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {_.get(subscriptionsQuery.data, "data", []).map((mobileSubscription: any) => {
            const emailSubscription = JsonApi.findById(
              _.get(emailSubscriptionsQuery, "data.json.data", []),
              mobileSubscription.id.toString(),
            ) as any;
            const isSubscribedMobile = mobileSubscription.attributes.subscribed;
            const isSubscribedEmail = emailSubscription.attributes.subscribed;
            const name = mobileSubscription.attributes.name;
            const id = mobileSubscription.id;
            return (
              <Table.Row key={id}>
                <Table.Cell textAlign='center'>
                  <Checkbox
                    checked={isSubscribedMobile}
                    name={name}
                    onChange={async (e, { checked }) => {
                      let response: any;
                      if (checked) {
                        response = await subscribe(id);
                      } else {
                        response = await unsubscribe(id);
                      }
                      if (response?.ok) {
                        addToast(`Your subscriptions have been updated`, { appearance: "success", autoDismiss: true });
                      } else {
                        addToast(`Hmm, we were unable to update your subscriptions`, {
                          appearance: "error",
                          autoDismiss: true,
                        });
                      }
                    }}
                  />
                </Table.Cell>
                <Table.Cell textAlign='center'>
                  <Checkbox
                    disabled={isEmailUnsubscribed}
                    checked={isSubscribedEmail}
                    name={name}
                    onChange={async (e, { checked }) => {
                      let response: any;
                      if (checked) {
                        response = await subscribeEmail({ id });
                      } else {
                        response = await unsubscribeEmail({ id });
                      }
                      if (response?.ok) {
                        addToast(`Your subscriptions have been updated`, { appearance: "success", autoDismiss: true });
                      } else {
                        addToast(`Hmm, we were unable to update your subscriptions`, {
                          appearance: "error",
                          autoDismiss: true,
                        });
                      }
                    }}
                  />
                </Table.Cell>
                <Table.Cell>{name}</Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </>
  );
};

export const SubscriptionGroups = () => {
  const { accessToken } = camelize(queryString.parse(window.location.search));
  const dispatch = useDispatch();
  const firstName = identitySelectors.getUserFirstName();
  const lastName = identitySelectors.getUserLastName();
  const email = identitySelectors.getUserEmail();
  const isLoggedIn = identitySelectors.isLoggedIn();
  const subject = useMemo(() => firstName || lastName || "there", [isLoggedIn]);

  useEffect(() => {
    if (accessToken) {
      dispatch(
        setJwt({
          accessToken,
        }),
      );
    }
  }, [accessToken]);

  if (!isLoggedIn) {
    if (!accessToken) {
      return <Message info>{`We weren't able to load your account, make sure you are logged in!`}</Message>;
    }
    return (
      <Grid verticalAlign='middle' centered>
        <div style={{ margin: "auto", height: "100%" }}>
          <Loader active={true}>Loading...</Loader>
        </div>
      </Grid>
    );
  }

  return (
    <ToastProvider>
      <Segment basic>
        <Header>
          <Header.Content>Modify your subscriptions</Header.Content>
          <Header.Subheader>
            {`Hey ${subject}, we'd love to get information to you that you care about`}
          </Header.Subheader>
        </Header>
        <SubscriptionsList email={email} />
      </Segment>
    </ToastProvider>
  );
};
